import { useState } from 'react'
import toast from 'react-hot-toast'
import {
    useAdminSizes,
    useCreateSize,
    useUpdateSize,
    useDeleteSize,
} from '@/api/useAdminCatalog'
import { Spinner } from '@/components/ui'
import type { PrintSize } from '@/api/usePrintSizes'
import styles from './AdminSizes.module.css'

interface SizeForm {
    name: string
    slug: string
    width_mm: number
    height_mm: number
    is_active: boolean
    sort_order: number
    allow_half_millar: boolean
}

const EMPTY_FORM: SizeForm = {
    name: '',
    slug: '',
    width_mm: 0,
    height_mm: 0,
    is_active: true,
    sort_order: 0,
    allow_half_millar: false,
}

export function AdminSizes() {
    const { data: sizes, isLoading } = useAdminSizes()
    const createSize = useCreateSize()
    const updateSize = useUpdateSize()
    const deleteSize = useDeleteSize()

    const [showAddRow, setShowAddRow] = useState(false)
    const [addForm, setAddForm] = useState<SizeForm>(EMPTY_FORM)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editForm, setEditForm] = useState<SizeForm>(EMPTY_FORM)

    const handleAddSave = () => {
        createSize.mutate(addForm, {
            onSuccess: () => {
                toast.success('Tamaño creado')
                setShowAddRow(false)
                setAddForm(EMPTY_FORM)
            },
            onError: () => toast.error('Error al crear tamaño'),
        })
    }

    const handleEditStart = (size: PrintSize) => {
        setEditingId(size.id)
        setEditForm({
            name: size.name,
            slug: size.slug,
            width_mm: size.width_mm,
            height_mm: size.height_mm,
            is_active: size.is_active,
            sort_order: size.sort_order,
            allow_half_millar: size.allow_half_millar,
        })
    }

    const handleEditSave = (id: number) => {
        updateSize.mutate(
            { id, ...editForm },
            {
                onSuccess: () => {
                    toast.success('Tamaño actualizado')
                    setEditingId(null)
                },
                onError: () => toast.error('Error al actualizar tamaño'),
            }
        )
    }

    const handleDelete = (id: number, name: string) => {
        if (!window.confirm(`¿Eliminar el tamaño "${name}"?`)) return
        deleteSize.mutate(id, {
            onSuccess: () => toast.success('Tamaño eliminado'),
            onError: () => toast.error('Error al eliminar tamaño'),
        })
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Tamaños de Impresión</h1>
                <button
                    className={styles.addButton}
                    onClick={() => {
                        setShowAddRow(true)
                        setAddForm(EMPTY_FORM)
                        setEditingId(null)
                    }}
                >
                    + Agregar Tamaño
                </button>
            </div>

            {isLoading ? (
                <div className={styles.loading}>
                    <Spinner size="lg" />
                </div>
            ) : (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Slug</th>
                            <th>Ancho (mm)</th>
                            <th>Alto (mm)</th>
                            <th>Activo</th>
                            <th>Medio millar</th>
                            <th>Orden</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showAddRow && (
                            <tr className={styles.formRow}>
                                <td>
                                    <input
                                        className={styles.formInput}
                                        value={addForm.name}
                                        onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                                        placeholder="Nombre"
                                    />
                                </td>
                                <td>
                                    <input
                                        className={styles.formInput}
                                        value={addForm.slug}
                                        onChange={(e) => setAddForm({ ...addForm, slug: e.target.value })}
                                        placeholder="slug"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        className={styles.formInput}
                                        value={addForm.width_mm}
                                        onChange={(e) => setAddForm({ ...addForm, width_mm: Number(e.target.value) })}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        className={styles.formInput}
                                        value={addForm.height_mm}
                                        onChange={(e) => setAddForm({ ...addForm, height_mm: Number(e.target.value) })}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        className={styles.formCheckbox}
                                        checked={addForm.is_active}
                                        onChange={(e) => setAddForm({ ...addForm, is_active: e.target.checked })}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        className={styles.formCheckbox}
                                        checked={addForm.allow_half_millar}
                                        onChange={(e) => setAddForm({ ...addForm, allow_half_millar: e.target.checked })}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        className={styles.formInput}
                                        value={addForm.sort_order}
                                        onChange={(e) => setAddForm({ ...addForm, sort_order: Number(e.target.value) })}
                                    />
                                </td>
                                <td>
                                    <div className={styles.actions}>
                                        <button
                                            className={styles.saveBtn}
                                            onClick={handleAddSave}
                                            disabled={createSize.isPending}
                                        >
                                            Guardar
                                        </button>
                                        <button
                                            className={styles.cancelBtn}
                                            onClick={() => setShowAddRow(false)}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!sizes || sizes.length === 0 ? (
                            <tr>
                                <td colSpan={8} className={styles.empty}>
                                    No hay tamaños registrados
                                </td>
                            </tr>
                        ) : (
                            sizes.map((size) =>
                                editingId === size.id ? (
                                    <tr key={size.id} className={styles.formRow}>
                                        <td>
                                            <input
                                                className={styles.formInput}
                                                value={editForm.name}
                                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className={styles.formInput}
                                                value={editForm.slug}
                                                onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                className={styles.formInput}
                                                value={editForm.width_mm}
                                                onChange={(e) => setEditForm({ ...editForm, width_mm: Number(e.target.value) })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                className={styles.formInput}
                                                value={editForm.height_mm}
                                                onChange={(e) => setEditForm({ ...editForm, height_mm: Number(e.target.value) })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className={styles.formCheckbox}
                                                checked={editForm.is_active}
                                                onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className={styles.formCheckbox}
                                                checked={editForm.allow_half_millar}
                                                onChange={(e) => setEditForm({ ...editForm, allow_half_millar: e.target.checked })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                className={styles.formInput}
                                                value={editForm.sort_order}
                                                onChange={(e) => setEditForm({ ...editForm, sort_order: Number(e.target.value) })}
                                            />
                                        </td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.saveBtn}
                                                    onClick={() => handleEditSave(size.id)}
                                                    disabled={updateSize.isPending}
                                                >
                                                    Guardar
                                                </button>
                                                <button
                                                    className={styles.cancelBtn}
                                                    onClick={() => setEditingId(null)}
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={size.id}>
                                        <td>{size.name}</td>
                                        <td>{size.slug}</td>
                                        <td>{size.width_mm}</td>
                                        <td>{size.height_mm}</td>
                                        <td>
                                            <span className={`${styles.badge} ${size.is_active ? styles.badgeActive : styles.badgeInactive}`}>
                                                {size.is_active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td>
                                            {size.allow_half_millar && (
                                                <span className={`${styles.badge} ${styles.badgeActive}`}>Sí</span>
                                            )}
                                        </td>
                                        <td>{size.sort_order}</td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.editBtn}
                                                    onClick={() => handleEditStart(size)}
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    className={styles.deleteBtn}
                                                    onClick={() => handleDelete(size.id, size.name)}
                                                    disabled={deleteSize.isPending}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            )
                        )}
                    </tbody>
                </table>
            )}
        </div>
    )
}
